// "Åka till …" och "Resa bort" (b3 avsnitt 3 E). Tiderna skrivs i platsens egen tid, och
// molnet räknar om dem med platsens tidszon. Efter "Spara" visas planen som molnet har räknat
// fram, till exempel "Trysil ska vara varmt fredag 18.00. Värmen går på Komfort torsdag 22.00."
// Med styrningen av står det vad som skulle ha gjorts.

import type { Plantyp, Platsinfo } from "@batkoll/gemensam";
import { useState } from "preact/hooks";
import { api, Felsvar } from "../api.ts";
import { TYPNAMN } from "./start.tsx";

type Resultat = { plats: { id: string; namn: string }; plan: { rader: string[] } }[];

export function Resvy(props: { typ: Plantyp; platser: Platsinfo[] }) {
  const egna = props.platser.filter((p) => p.roll === "agare");
  const akaTill = props.typ === "aka_till";
  const [valda, setValda] = useState<string[]>(akaTill && egna[0] ? [egna[0].id] : []);
  const [fran, setFran] = useState("");
  const [till, setTill] = useState("");
  const [fel, setFel] = useState<string | null>(null);
  const [sparar, setSparar] = useState(false);
  const [resultat, setResultat] = useState<Resultat | null>(null);
  const tidszoner = [...new Set(egna.filter((p) => valda.includes(p.id)).map((p) => p.tidszon))];

  async function spara(e: Event) {
    e.preventDefault();
    setFel(null);
    setSparar(true);
    try {
      const r = await api.resa(props.typ, valda, fran, till || null);
      setResultat(r.planer);
    } catch (x) {
      setFel(x instanceof Felsvar ? x.message : "Det gick inte att spara planen.");
    } finally {
      setSparar(false);
    }
  }

  if (resultat) {
    return (
      <section class="kort resa" aria-label="Planen är sparad">
        <h2>Planen är sparad</h2>
        {resultat.map((r) => (
          <ul key={r.plats.id} class="plan">
            {r.plan.rader.map((rad) => (
              <li key={rad}>{rad}</li>
            ))}
          </ul>
        ))}
        <a class="knapp-huvud bred knapplank" href="#start">
          Till översikten
        </a>
      </section>
    );
  }

  return (
    <section class="kort resa" aria-label={akaTill ? "Åka till" : "Resa bort"}>
      <h2>{akaTill ? "Åka till …" : "Resa bort"}</h2>
      <p class="liten">
        {akaTill
          ? "Välj platsen och när du kommer. Båtkoll ser till att det är klart när du kommer."
          : "Välj platserna och perioden. Husen går i bortaläge under tiden."}
      </p>
      <form onSubmit={spara}>
        {akaTill ? (
          <label>
            Plats
            <select value={valda[0] ?? ""} onChange={(e) => setValda([e.currentTarget.value])}>
              {egna.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.namn} ({TYPNAMN[p.typ].toLowerCase()})
                </option>
              ))}
            </select>
          </label>
        ) : (
          <fieldset class="platsval">
            <legend>Platser</legend>
            {egna.map((p) => (
              <label key={p.id} class="kryss">
                <input
                  type="checkbox"
                  checked={valda.includes(p.id)}
                  onChange={(e) =>
                    setValda((v) =>
                      e.currentTarget.checked ? [...v, p.id] : v.filter((x) => x !== p.id),
                    )
                  }
                />
                {p.namn}
              </label>
            ))}
          </fieldset>
        )}
        <label>
          {akaTill ? "Ankomst" : "Från"}
          <input
            type="datetime-local"
            required
            value={fran}
            onInput={(e) => setFran(e.currentTarget.value)}
          />
        </label>
        <label>
          {akaTill ? "Avresa (om du vill)" : "Till"}
          <input
            type="datetime-local"
            required={!akaTill}
            value={till}
            onInput={(e) => setTill(e.currentTarget.value)}
          />
        </label>
        {tidszoner.length > 0 && (
          <p class="liten">Tiderna är lokal tid på platsen ({tidszoner.join(", ")}).</p>
        )}
        {fel && (
          <p class="fel" role="alert">
            {fel}
          </p>
        )}
        <div class="knapprad">
          <button class="knapp-huvud" type="submit" disabled={sparar || valda.length === 0}>
            Spara planen
          </button>
          <a class="knapp-andra knapplank" href="#start">
            Avbryt
          </a>
        </div>
      </form>
    </section>
  );
}
